import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookletViewPage = () => {
  const { bookletId } = useParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [booklet, setBooklet] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  useEffect(() => {
    fetchBookletDetails();
  }, [bookletId]);

  const fetchBookletDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/questions/booklets/${bookletId}`, {
        withCredentials: true
      });
      
      setBooklet(response.data.booklet);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching booklet:', error);
      alert(t('error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!booklet) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-xl text-gray-600">
            {t('booklet_not_found')}
          </p>
          <button
            onClick={() => navigate('/booklet-designer')}
            className="mt-4 text-blue-600 hover:underline"
          >
            {t('go_back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={booklet.title} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Booklet Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{booklet.title}</h1>
              <div className="flex items-center space-x-6 text-gray-600">
                <span>📚 {questions.length} {t('questions')}</span>
                <span>⏱️ {booklet.estimated_time_minutes} {t('minutes')}</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                  {t('week')} {booklet.week_number}
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate(`/booklet/${bookletId}/take`)}
              disabled={questions.length === 0}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-xl font-bold hover:from-green-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              🚀 {t('start_test')}
            </button>
          </div>

          {/* Learning Objectives */}
          {booklet.learning_objectives && booklet.learning_objectives.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('learning_objectives')}:
              </h3>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {booklet.learning_objectives.map((obj, idx) => {
                  // Display objectives that contain either language, but filter duplicates
                  const isTurkish = obj.includes('pratik') || obj.includes('Fizik') || obj.includes('Matematik') || obj.includes('Kimya') || obj.includes('Biyoloji');
                  const isEnglish = obj.includes('Practice') || obj.includes('Physics') || obj.includes('Mathematics') || obj.includes('Chemistry') || obj.includes('Biology');
                  
                  if (language === 'tr' && isTurkish) return <li key={idx}>{obj}</li>;
                  if (language === 'en' && isEnglish) return <li key={idx}>{obj}</li>;
                  if (!isTurkish && !isEnglish) return <li key={idx}>{obj}</li>;
                  return null;
                })}
              </ul>
            </div>
          )}

          {/* Sections */}
          {booklet.sections && booklet.sections.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('sections')}:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {booklet.sections.map((section, idx) => {
                  const sectionTitle = language === 'tr' 
                    ? (section.section_title_tr || section.section_title) 
                    : (section.section_title_en || section.section_title);
                  const description = language === 'tr'
                    ? (section.description_tr || section.description)
                    : (section.description_en || section.description);
                  
                  return (
                    <div key={idx} className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                      <h4 className="font-bold text-gray-800 mb-1">{sectionTitle}</h4>
                      <p className="text-sm text-gray-600 mb-2">{description}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>📝 {section.question_ids?.length || 0} {t('questions')}</span>
                        <span>⏱️ {section.estimated_time_minutes} {t('minutes')}</span>
                        <span className="bg-gray-200 px-2 py-1 rounded">{section.mode}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Questions List */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {t('questions')}
          </h2>

          {questions.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-lg">
                {t('no_questions_in_booklet')}
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {t('make_sure_questions')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {questions.map((question, idx) => (
                <div
                  key={question.id}
                  className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedQuestion(question)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">
                        {question.subject}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      {question.exam_context?.map((ctx, i) => (
                        <span key={i} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {ctx}
                        </span>
                      ))}
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                        {question.difficulty}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-800 font-medium line-clamp-2">
                    {language === 'tr' ? question.stem_TR : question.stem_EN || question.stem_TR}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reflection Prompts */}
        {booklet.reflection_prompts && booklet.reflection_prompts.length > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 mt-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              💭 {t('reflection_questions')}
            </h2>
            <ul className="space-y-3">
              {booklet.reflection_prompts.map((prompt, idx) => {
                // Split bilingual prompts (format: "Turkish text / English text")
                const parts = prompt.split(' / ');
                const displayText = language === 'tr' 
                  ? (parts.length > 1 ? parts[0] : prompt)
                  : (parts.length > 1 ? parts[1] : prompt);
                
                return (
                  <li key={idx} className="flex items-start">
                    <span className="text-purple-600 mr-2">•</span>
                    <span className="text-gray-700">{displayText}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Question Detail Modal */}
      {selectedQuestion && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" 
          onClick={() => setSelectedQuestion(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{selectedQuestion.subject}</h2>
              <button onClick={() => setSelectedQuestion(null)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">{t('question')}:</h3>
                <p className="text-gray-800">
                  {language === 'tr' ? selectedQuestion.stem_TR : selectedQuestion.stem_EN || selectedQuestion.stem_TR}
                </p>
              </div>
              {selectedQuestion.options?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">{t('options')}:</h3>
                  <ul className="space-y-2">
                    {selectedQuestion.options.map((option, idx) => (
                      <li key={idx} className="text-gray-700 bg-gray-50 p-2 rounded">{option}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">{t('correct_answer')}:</h3>
                <p className="text-green-600 font-semibold bg-green-50 p-3 rounded">{selectedQuestion.correct_answer}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">{t('solution')}:</h3>
                <p className="text-gray-700 whitespace-pre-wrap bg-blue-50 p-4 rounded">{selectedQuestion.solution_full}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookletViewPage;
